// ============================================
// JOB DETAIL PAGE
// ============================================

import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { ArrowLeft, Ship, Heart, Loader2, Check, AlertTriangle, User } from 'lucide-react';
import { useJob, useApplyToJob, useFavoriteJobs, useAddFavoriteJob, useRemoveFavoriteJob, useMyApplications, useMyProfile, useCheckCanApply, useJobMatchScore } from '@/hooks';
import { formatCurrency, formatDate } from '@/utils';
import { MatchScoreBadge } from '@/components';
import * as Dialog from '@radix-ui/react-dialog';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from '@crew-at-sea/i18n';
import { commonApi } from '@crew-at-sea/api-client';

export const Route = createFileRoute('/_authenticated/jobs/$slug')({
  component: JobDetailPage,
});

function JobDetailPage() {
  const { t, i18n } = useTranslation(['panel', 'common', 'enums']);
  const params = Route.useParams() as { slug: string };
  const slug = params.slug;
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [showProfileWarning, setShowProfileWarning] = useState(false);
  const [uploadedCvUrl, setUploadedCvUrl] = useState('');
  const [uploadingCv, setUploadingCv] = useState(false);

  const { data: jobResponse, isLoading } = useJob(slug);
  const { data: favoritesResponse } = useFavoriteJobs();
  const { data: applicationsResponse } = useMyApplications();
  const addFavorite = useAddFavoriteJob();
  const removeFavorite = useRemoveFavoriteJob();
  const applyMutation = useApplyToJob();

  const job: any = (jobResponse as any)?.data || jobResponse;
  const favorites: any[] = (favoritesResponse as any)?.data || favoritesResponse || [];
  const applications: any[] = (applicationsResponse as any)?.data || applicationsResponse || [];
  
  const { data: profileResponse } = useMyProfile();
  const profile: any = (profileResponse as any)?.data || profileResponse;
  const profileCompleteness = profile?.profileCompleteness || 0;

  const { data: matchScoreResponse } = useJobMatchScore(job?.id || '');
  const matchScoreData: any = (matchScoreResponse as any)?.data || matchScoreResponse;
  const matchScore = matchScoreData?.matchScore?.overallScore || 0;

  const { data: canApplyResponse, isLoading: canApplyLoading } = useCheckCanApply(job?.id || '');
  const canApplyData: any = (canApplyResponse as any)?.data || canApplyResponse;

  const getFullUrl = (path: string | null | undefined) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `${import.meta.env.VITE_API_URL?.replace('/api/v1', '')}${path.startsWith('/') ? '' : '/'}${path}`;
  };

  const favoriteJobIds = favorites.map((f: any) => f.job?.id || f.jobId);
  const isFavorite = job && favoriteJobIds.includes(job.id);
  const hasApplied = job && applications.some((a: any) => a.job?.id === job.id || a.jobId === job.id);

  const { register, handleSubmit, reset } = useForm();

  const toggleFavorite = () => {
    if (!job) return;
    isFavorite ? removeFavorite.mutate(job.id) : addFavorite.mutate(job.id);
  };

  const handleApplyClick = () => {
    if (!canApplyData) {
      setIsApplyModalOpen(true);
      return;
    }
    if (!canApplyData.profileComplete) {
      setShowProfileWarning(true);
      return;
    }
    if (canApplyData.alreadyApplied) {
      toast.error(t('panel:jobDetail.alreadyApplied'));
      return;
    }
    if (!canApplyData.jobActive) {
      toast.error(t('panel:jobDetail.jobInactive'));
      return;
    }
    setIsApplyModalOpen(true);
  };

  const handleCvUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error(t('common:errors.validation.invalidFileType', { defaultValue: 'Lütfen sadece PDF dosyası seçin.' }));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('documents.fileTooLarge', { defaultValue: 'Dosya boyutu 10MB\'dan küçük olmalıdır.' }));
      return;
    }
    setUploadingCv(true);
    try {
      const result = await commonApi.uploadDocument(file);
      const fileUrl = result?.url || (result as any)?.data?.url;
      if (!fileUrl) throw new Error('No URL returned');
      setUploadedCvUrl(fileUrl);
    } catch (err) {
      console.error('CV upload error:', err);
      toast.error(t('profile.toast.pdfUploadError', { defaultValue: 'PDF yüklenirken hata oluştu.' }));
    } finally {
      setUploadingCv(false);
      e.target.value = '';
    }
  };

  const onApply = (data: any) => {
    if (!job) return;
    const payload: any = { jobId: job.id, ...data };
    if (uploadedCvUrl) {
      // Freshly uploaded CV takes precedence over the profile CV.
      payload.cvUrl = uploadedCvUrl;
      payload.includeCv = false;
    }
    applyMutation.mutate(payload, {
      onSuccess: () => {
        setIsApplyModalOpen(false);
        reset();
        setUploadedCvUrl('');
        toast.success(t('panel:jobDetail.applySuccess'));
      },
      onError: (error: any) => {
        if (error?.response?.data?.code === 'PROFILE_INCOMPLETE') {
          setShowProfileWarning(true);
          setIsApplyModalOpen(false);
        } else {
          toast.error(error?.response?.data?.message || t('panel:jobDetail.applyError'));
        }
      },
    });
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-maritime-600" /></div>;
  }

  if (!job) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">{t('panel:jobDetail.notFound')}</h2>
        <Link to="/jobs" className="text-maritime-600 mt-2 inline-block">{t('panel:jobDetail.backToJobs')}</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Link to="/jobs" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-4 h-4" />{t('panel:jobDetail.backToJobs')}
      </Link>

      {/* Profile Incomplete Warning */}
      {showProfileWarning && canApplyData && !canApplyData.profileComplete && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-maritime p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-yellow-800">{t('panel:jobDetail.completeProfile')}</h3>
              <p className="text-sm text-yellow-700 mt-1">
                {t('panel:jobDetail.profileRequirement', { percent: canApplyData.profilePercentage })}
              </p>
              <div className="mt-3 flex items-center gap-3">
                <div className="flex-1 bg-yellow-200 rounded-full h-2">
                  <div className="bg-yellow-600 h-2 rounded-full transition-all" style={{ width: `${canApplyData.profilePercentage}%` }} />
                </div>
                <span className="text-sm font-medium text-yellow-800">
                  {canApplyData.profilePercentage}% / 80%
                </span>
              </div>
              <Link to="/profile" className="inline-flex items-center gap-2 mt-3 text-sm font-medium text-yellow-800 hover:text-yellow-900">
                <User className="w-4 h-4" />{t('panel:jobDetail.goToProfile')}
              </Link>
            </div>
            <button onClick={() => setShowProfileWarning(false)} className="text-yellow-600 hover:text-yellow-800">×</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Header */}
          <div className="card p-6">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-maritime bg-maritime-100 flex items-center justify-center">
                <Ship className="w-8 h-8 text-maritime-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">{job.title}</h1>
                    <Link to="/companies/$slug" params={{ slug: job.company?.slug }} className="text-maritime-600 hover:underline">
                      {job.company?.name}
                    </Link>
                    {job.rank && (
                      <p className="text-sm text-gray-500 mt-1">
                        {i18n.language === 'en' ? (job.rank.nameEn || job.rank.nameTr) : (job.rank.nameTr || job.rank.nameEn)}
                      </p>
                    )}
                  </div>
                  {matchScore > 0 && (
                    <div className="flex-shrink-0">
                      <MatchScoreBadge score={matchScore} size="lg" showIcon />
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {job.vesselType && <span className="badge">{t(`enums:vesselType.${job.vesselType}`, { defaultValue: job.vesselType })}</span>}
                  {job.department && <span className="badge">{t(`enums:department.${job.department}`, { defaultValue: job.department })}</span>}
                </div>
              </div>
            </div>

            {/* Job Details Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-100">
              {job.rank && (
                <div>
                  <p className="text-sm text-gray-500">{t('panel:jobDetail.position')}</p>
                  <p className="font-semibold">{i18n.language === 'en' ? (job.rank.nameEn || job.rank.nameTr) : (job.rank.nameTr || job.rank.nameEn)}</p>
                </div>
              )}
              {job.department && (
                <div>
                  <p className="text-sm text-gray-500">{t('panel:jobDetail.department')}</p>
                  <p className="font-semibold">{t(`enums:department.${job.department}`, { defaultValue: job.department })}</p>
                </div>
              )}
              {job.contractDuration && (
                <div>
                  <p className="text-sm text-gray-500">{t('panel:jobDetail.contract')}</p>
                  <p className="font-semibold">{t('panel:jobs.monthsContract', { count: job.contractDuration })}</p>
                </div>
              )}
              {(job.salaryMin || job.salaryMax) && (
                <div>
                  <p className="text-sm text-gray-500">{t('panel:jobDetail.salary')}</p>
                  <p className="font-semibold">
                    {job.salaryMin && job.salaryMax
                      ? `${formatCurrency(job.salaryMin)} - ${formatCurrency(job.salaryMax)}`
                      : job.salaryMin ? `${formatCurrency(job.salaryMin)}+` : formatCurrency(job.salaryMax)}
                  </p>
                </div>
              )}
              {job.joinDate && (
                <div>
                  <p className="text-sm text-gray-500">{t('panel:jobDetail.embarkation')}</p>
                  <p className="font-semibold">{formatDate(job.joinDate)}</p>
                </div>
              )}
              {job.experienceMin !== null && job.experienceMin !== undefined && (
                <div>
                  <p className="text-sm text-gray-500">{t('panel:jobDetail.experience')}</p>
                  <p className="font-semibold">{t('panel:jobDetail.yearsPlus', { count: job.experienceMin })}</p>
                </div>
              )}
              {job.preferredNationality && (
                <div>
                  <p className="text-sm text-gray-500">{t('panel:jobDetail.preferredNationality')}</p>
                  <p className="font-semibold">{job.preferredNationality}</p>
                </div>
              )}
              {job.crewCount && (
                <div>
                  <p className="text-sm text-gray-500">{t('panel:jobDetail.crewCount')}</p>
                  <p className="font-semibold">{job.crewCount}</p>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          {job.description && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('panel:jobDetail.description')}</h2>
              <div className="prose prose-sm max-w-none text-gray-600 whitespace-pre-line">{job.description}</div>
            </div>
          )}

          {/* Requirements */}
          {job.requirements && job.requirements.length > 0 && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('panel:jobDetail.requirements')}</h2>
              <ul className="space-y-2">
                {job.requirements.map((req: any, index: number) => (
                  <li key={index} className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">
                      {req.certificateType?.nameTr || req.certificateType?.nameEn || req.certificateType?.name || req.description}
                      {req.isRequired && <span className="text-red-500 ml-1">*</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Vessel Info */}
          {(job.vesselName || job.imoNumber || job.vesselDWT || job.vesselBuiltYear || job.engineBrand || job.engineKw || job.vesselFlag || job.hasEcdis !== null || job.hasSatelliteInternet !== null) && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('panel:jobDetail.vesselInfo')}</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {job.vesselName && <InfoItem label={t('panel:jobDetail.vesselName')} value={job.vesselName} />}
                {job.vesselFlag && <InfoItem label={t('panel:jobDetail.vesselFlag')} value={job.vesselFlag} />}
                {job.imoNumber && <InfoItem label="IMO" value={job.imoNumber} />}
                {job.vesselDWT && <InfoItem label="DWT" value={job.vesselDWT.toLocaleString()} />}
                {job.vesselBuiltYear && <InfoItem label={t('panel:jobDetail.builtYear')} value={job.vesselBuiltYear.toString()} />}
                {job.engineBrand && <InfoItem label={t('panel:jobDetail.mainEngine')} value={job.engineBrand} />}
                {job.engineKw && <InfoItem label={t('panel:jobDetail.enginePower')} value={`${job.engineKw.toLocaleString()} KW`} />}
                {job.hasEcdis !== null && job.hasEcdis !== undefined && (
                  <InfoItem label="ECDIS" value={job.hasEcdis ? t('panel:jobDetail.available') : t('panel:jobDetail.notAvailable')} />
                )}
                {job.hasSatelliteInternet !== null && job.hasSatelliteInternet !== undefined && (
                  <InfoItem label={t('panel:jobDetail.satelliteInternet')} value={job.hasSatelliteInternet ? t('panel:jobDetail.available') : t('panel:jobDetail.notAvailable')} />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="card p-6 sticky top-20">
            {hasApplied ? (
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
                <p className="font-medium text-gray-900">{t('panel:jobDetail.alreadyAppliedInfo')}</p>
                <Link to="/applications" className="text-sm text-maritime-600 hover:underline">
                  {t('panel:applications.title')}
                </Link>
              </div>
            ) : (
              <>
                {canApplyData && !canApplyData.profileComplete && (
                  <div className="mb-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <div className="flex items-center gap-2 text-sm text-yellow-800">
                      <AlertTriangle className="w-4 h-4" />
                      <span>{t('panel:jobDetail.profilePercent', { percent: canApplyData.profilePercentage })}</span>
                    </div>
                    <div className="mt-2 bg-yellow-200 rounded-full h-1.5">
                      <div className="bg-yellow-600 h-1.5 rounded-full" style={{ width: `${canApplyData.profilePercentage}%` }} />
                    </div>
                    <p className="text-xs text-yellow-700 mt-2">{t('panel:jobDetail.profileMinRequired')}</p>
                  </div>
                )}
                <button 
                  onClick={handleApplyClick} 
                  className={`btn-primary w-full mb-3 ${canApplyData && !canApplyData.profileComplete ? 'opacity-75' : ''}`}
                  disabled={canApplyLoading}
                >
                  {canApplyLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : canApplyData && !canApplyData.profileComplete ? (
                    <><AlertTriangle className="w-4 h-4" />{t('panel:jobDetail.applyProfileIncomplete')}</>
                  ) : (
                    t('panel:jobs.apply')
                  )}
                </button>
                <button onClick={toggleFavorite} className={`btn-secondary w-full ${isFavorite ? 'text-red-500' : ''}`}>
                  <Heart className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
                  {isFavorite ? t('panel:jobDetail.inFavorites') : t('panel:jobDetail.addToFavorites')}
                </button>
              </>
            )}

            {/* Company Info */}
            {job.company && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-3">{t('panel:jobDetail.companyInfo')}</h3>
                <div className="flex items-center gap-3">
                  {job.company.logoUrl ? (
                    <img src={job.company.logoUrl} alt={job.company.name} className="w-12 h-12 rounded-lg object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                      <Ship className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <Link to="/companies/$slug" params={{ slug: job.company.slug }} className="font-medium text-gray-900 hover:text-maritime-600">
                      {job.company.name}
                    </Link>
                    <p className="text-sm text-gray-500">{job.company.country}</p>
                  </div>
                </div>
                {job.company.isVerified && (
                  <div className="mt-3 flex items-center gap-2 text-sm text-green-600">
                    <Check className="w-4 h-4" />{t('panel:jobDetail.verifiedCompany')}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Apply Modal */}
      <Dialog.Root open={isApplyModalOpen} onOpenChange={(open) => { setIsApplyModalOpen(open); if (!open) setUploadedCvUrl(''); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-xl w-full max-w-md z-50 p-6">
            <Dialog.Title className="text-lg font-semibold text-gray-900 mb-4">{t('panel:jobDetail.applyTitle')}</Dialog.Title>
            <form onSubmit={handleSubmit(onApply)} className="space-y-4">
              <div>
                <label className="label">{t('panel:jobDetail.coverLetter')}</label>
                <textarea className="input" rows={4} placeholder={t('panel:jobDetail.coverLetterPlaceholder')} {...register('coverLetter')} />
              </div>
              <div>
                <label className="label">{t('panel:jobDetail.availableFrom')}</label>
                <input type="date" className="input" {...register('availableFrom')} />
              </div>
              <div>
                <label className="label">{t('panel:jobDetail.expectedSalary')}</label>
                <input type="number" className="input" placeholder="5000" {...register('expectedSalary', { valueAsNumber: true })} />
              </div>
              {/* CV: profildeki CV'yi ekle veya yeni dosya yükle */}
              <div className="space-y-3 pt-2">
                <label className="label">{t('panel:jobDetail.cvLabel', { defaultValue: 'CV' })}</label>

                {profile?.cvPdfUrl && !uploadedCvUrl && (
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="includeCv" className="w-4 h-4 text-maritime-600 rounded border-gray-300 focus:ring-maritime-600" {...register('includeCv')} />
                    <label htmlFor="includeCv" className="text-sm text-gray-700">
                      {t('panel:jobDetail.includeCv', { defaultValue: 'Profildeki CV\'yi ekle' })}
                    </label>
                    <a href={getFullUrl(profile.cvPdfUrl) || '#'} target="_blank" rel="noopener noreferrer" className="text-sm text-maritime-600 hover:underline ml-auto">
                      {t('common:view')}
                    </a>
                  </div>
                )}

                {uploadedCvUrl ? (
                  <div className="flex items-center gap-2 text-sm text-green-700">
                    <Check className="w-4 h-4" />
                    <span>{t('panel:jobDetail.cvUploaded', { defaultValue: 'Yeni CV yüklendi' })}</span>
                    <button type="button" onClick={() => setUploadedCvUrl('')} className="text-sm text-red-500 hover:text-red-600 ml-auto">
                      {t('common:remove', { defaultValue: 'Kaldır' })}
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center gap-2 text-sm text-maritime-600 cursor-pointer hover:underline">
                    {uploadingCv ? (
                      <><Loader2 className="w-4 h-4 animate-spin" />{t('common:loading', { defaultValue: 'Yükleniyor...' })}</>
                    ) : (
                      <span>{t('panel:jobDetail.uploadNewCv', { defaultValue: '+ Yeni CV yükle (PDF)' })}</span>
                    )}
                    <input type="file" accept="application/pdf" className="hidden" disabled={uploadingCv} onChange={handleCvUpload} />
                  </label>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Dialog.Close asChild>
                  <button type="button" className="btn-secondary">{t('common:cancel')}</button>
                </Dialog.Close>
                <button type="submit" className="btn-primary" disabled={applyMutation.isPending}>
                  {applyMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {t('panel:jobs.apply')}
                </button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="font-medium text-gray-900">{value}</p>
    </div>
  );
}
